import { useState, useEffect } from 'react'

import CircleLoader from '@components/CircleLoader'
import ErrorMessage from '@components/ErrorMessage'
import Pin from '@components/Pin'

import { useOnUserChange } from 'auth/hooks'

import { locationDetailsSelector, locationDetailsPendingSelector, vehiclesSelector } from 'booking/selectors'
import { Map } from 'booking/components/Map'
import { ParkingInfoSideBar } from 'booking/components/ParkingInfoSideBar'
import { getBookingLocationDetails } from 'booking/actions'

import { loadOnMount } from 'shared/hoc'
import { isDesktop } from 'shared/responsive-helpers'
import { MarkerStyle } from 'shared/generate-marker-style'
import featureToggles from 'shared/featureToggles'
import { useConfirm } from 'shared/hooks/useConfirm'
import scrollTop from 'shared/scroll-top'

import VehicleSelection from 'purchase-flow/components/VehicleSelection'

import {
    FLOW_STEP_DELIVERY,
    FLOW_STEP_DATE,
    FLOW_STEP_PRODUCT,
    FLOW_STEP_REVIEW,
    FLOW_STEP_SUCCESS,
    FLOW_STEP_VEHICLE,
    CREDENTIAL_LPR,
    CREDENTIAL_PHYSICAL,
} from 'purchase-flow/constants'

import {
    locationNameSelector,
    subscriptionLocationErrorSelector,
    subscriptionLocationLoadingSelector,
    purchaseDataSelector,
    credentialTypeSelector,
    collectAddressLoadingSelector,
} from '../selectors'

import {
    getSubscriptionLocation,
    getOrderSummary,
    buySubscription,
    setPurchaseData,
    getCollectAddress,
} from '../actions'

import { limitMonths, useFlow as usePurchaseFlowSteps } from './utils'

import DateSelection from './DateSelection'
import ProductSelection from './ProductSelection'
import PurchaseFlowHeader from './PurchaseFlowHeader'
import DeliverySelection from './DeliverySelection'
import ReviewPaySelection from './ReviewPaySelection'
import SuccessPage from './SuccessPage'

import purchaseStyles from 'purchase-flow/styles.module.scss'
import styles from './styles.module.scss'

const SUBSCRIPTION_LITE_MONTHS_LIMIT = 1

const PurchaseFlow = ({
    locationName,
    location,
    locationPending,
    collectAddressLoading,
    subscriptionLocationLoading,
    errorMessage,
    getOrderSummary,
    buySubscription,
    purchaseData,
    setPurchaseData,
    credentialType,
    userVehicles,
    getCollectAddress,
}) => {
    const { confirm, ConfirmModal } = useConfirm()
    const [activeStep, setActiveStep] = useState(FLOW_STEP_DATE)

    const hasCredentialType = type => credentialType?.indexOf(type) > -1

    const hasDelivery = !featureToggles.hasSubcriptionLite || !hasCredentialType(CREDENTIAL_LPR)
    const flowSteps = usePurchaseFlowSteps(hasDelivery ? [] : [FLOW_STEP_DELIVERY])

    useEffect(() => {
        if (purchaseData.product) {
            setActiveStep(hasDelivery ? FLOW_STEP_DELIVERY : FLOW_STEP_REVIEW)
        } else if (purchaseData.startDate) {
            setActiveStep(FLOW_STEP_PRODUCT)
        }
    }, [])

    useEffect(() => {
        scrollTop()
    }, [activeStep])

    useOnUserChange(isLogin => {
        if (!isLogin && purchaseData.vehicles?.length > 0) {
            setPurchaseData({ ...purchaseData, vehicles: null })
            setActiveStep(FLOW_STEP_VEHICLE)
        }
    })

    const goNext = () => {
        const newIndex = flowSteps.indexOf(activeStep) + 1

        if (newIndex < flowSteps.length) {
            setActiveStep(flowSteps[newIndex])
        }

        switch (flowSteps[newIndex]) {
            case FLOW_STEP_DELIVERY:
                if (hasCredentialType(CREDENTIAL_PHYSICAL)) {
                    getCollectAddress()
                }
                break
            case FLOW_STEP_REVIEW:
                getOrderSummary({ ...purchaseData })
                break
        }
    }

    const setStartDate = startDate => {
        setPurchaseData({ ...purchaseData, startDate })
        goNext()
    }

    const setProduct = product => {
        setPurchaseData({ ...purchaseData, product })
        goNext()
    }

    const setVehicle = vehicles => {
        setPurchaseData({ ...purchaseData, vehicles })
        goNext()
    }

    const setDelivery = (delivery, deliveryAddress) => {
        setPurchaseData({ ...purchaseData, delivery, deliveryAddress })
        goNext()
    }

    const handleBuy = (paymentMethod, billingAddress, deliveryAddress, termsAcceptedDate) => {
        buySubscription({ ...purchaseData, paymentMethod, billingAddress, deliveryAddress, termsAcceptedDate })
    }

    const setSuccess = () => {
        setActiveStep(FLOW_STEP_SUCCESS)
    }

    const onStepClick = async step => {
        let { product, delivery } = purchaseData

        if (step === FLOW_STEP_PRODUCT) {
            delivery = null
        }

        if (step === FLOW_STEP_DATE) {
            const isConfirmed = await confirm()
            if (!isConfirmed) {
                return
            }

            product = null
            delivery = null
        }

        setActiveStep(step)
        setPurchaseData({ ...purchaseData, product, delivery })
    }

    const loading = subscriptionLocationLoading || collectAddressLoading

    return (
        <div className={styles.purchaseFlowContainer}>
            <ConfirmModal
                title="subscription.purchase.step.confirm.title"
                text="subscription.purchase.step.confirm.text"
            />
            <div className={styles.purchaseScreen}>
                {activeStep === FLOW_STEP_SUCCESS ? (
                    <SuccessPage />
                ) : (
                    <div className={purchaseStyles.flowContainer}>
                        {loading && <CircleLoader />}
                        {errorMessage && <ErrorMessage messages={[errorMessage]} />}
                        {!loading && !errorMessage && (
                            <div className={purchaseStyles.flowSection}>
                                <PurchaseFlowHeader
                                    activeStep={activeStep}
                                    stepValues={purchaseData}
                                    location={locationName}
                                    onStepClick={onStepClick}
                                    flowSteps={flowSteps}
                                />
                                {activeStep === FLOW_STEP_DATE && (
                                    <DateSelection
                                        onNext={setStartDate}
                                        value={purchaseData.startDate?.value}
                                        isOutsideRange={
                                            featureToggles.hasSubcriptionLite
                                                ? limitMonths(SUBSCRIPTION_LITE_MONTHS_LIMIT)
                                                : null
                                        }
                                    />
                                )}
                                {activeStep === FLOW_STEP_PRODUCT && (
                                    <ProductSelection
                                        onNext={setProduct}
                                        startDate={purchaseData.startDate}
                                        value={purchaseData.product}
                                    />
                                )}
                                {activeStep === FLOW_STEP_VEHICLE && (
                                    <VehicleSelection
                                        noLimit
                                        onNext={setVehicle}
                                        flowSelectedVehicles={purchaseData.vehicles || userVehicles}
                                    />
                                )}
                                {activeStep === FLOW_STEP_DELIVERY && (
                                    <DeliverySelection
                                        onNext={setDelivery}
                                        startDate={purchaseData.startDate?.displayValue}
                                        value={purchaseData.delivery}
                                    />
                                )}
                                {activeStep === FLOW_STEP_REVIEW && !errorMessage && (
                                    <ReviewPaySelection
                                        onSuccess={setSuccess}
                                        purchaseData={purchaseData}
                                        handleBuy={handleBuy}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isDesktop() && Object.keys(location).length > 0 && !loading && (
                <div className={purchaseStyles.sidebar}>
                    <ParkingInfoSideBar loading={locationPending} location={location}>
                        {location.geoLocation && (
                            <Map geoLocation={location.geoLocation}>
                                <div style={MarkerStyle(30, 30)}>
                                    <Pin name={location.name} />
                                </div>
                            </Map>
                        )}
                    </ParkingInfoSideBar>
                </div>
            )}
        </div>
    )
}

export default compose(
    connect(
        createStructuredSelector({
            locationName: locationNameSelector,
            errorMessage: subscriptionLocationErrorSelector,
            subscriptionLocationLoading: subscriptionLocationLoadingSelector,
            locationPending: locationDetailsPendingSelector,
            collectAddressLoading: collectAddressLoadingSelector,
            location: locationDetailsSelector,
            credentialType: credentialTypeSelector,
            purchaseData: purchaseDataSelector,
            userVehicles: vehiclesSelector,
        }),
        {
            getSubscriptionLocation,
            getBookingLocationDetails,
            getOrderSummary,
            buySubscription,
            setPurchaseData,
            getCollectAddress,
        }
    ),
    loadOnMount('getSubscriptionLocation', ({ match }) => [match?.params?.locationCode]),
    loadOnMount('getBookingLocationDetails', ({ match }) => [match?.params?.locationCode])
)(PurchaseFlow)
